import { useState, useMemo } from "react";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { AuthPageChrome } from "../../components/auth/AuthPageChrome";
import { ROUTES } from "../../constants";
import { apiService } from "../../services/api.service";
import { validators } from "../../utils/validators.util";
import type { InstitutionKind } from "../../types/institution.types";
import {
  Building2, CheckCircle2, Loader2, Mail, MapPin, Phone, User,
  Sparkles, Hash, BadgeCheck,
  Eye, EyeOff, Lock, Brain, BarChart3, GraduationCap, Shield,
  ArrowRight,
} from "lucide-react";
import mascoteAnotando from "../../assets/mascote3d/MascoteAnotando.svg";

// ── Constants ──────────────────────────────────────────────────────────────

const KIND_OPTIONS: { value: InstitutionKind; label: string }[] = [
  { value: "private_school", label: "Escola / colégio privado" },
  { value: "network", label: "Rede ou mantenedora" },
  { value: "university", label: "Ensino superior" },
  { value: "other", label: "Outro" },
];
const STUDENT_BANDS = [
  { value: "0-200", label: "Até 200 alunos" },
  { value: "200-500", label: "200 a 500" },
  { value: "500-1500", label: "500 a 1.500" },
  { value: "1500+", label: "Mais de 1.500" },
  { value: "unknown", label: "Prefiro informar depois" },
];
const PLAN_LABELS: Record<string, string> = {
  campus: "Neuroment Campus",
  educacao: "Neuroment Educação",
  rede: "Neuroment Rede",
};

// ── Input masks ────────────────────────────────────────────────────────────

function digitsOnly(s: string) { return s.replace(/\D/g, ""); }

function formatCNPJ(v: string) {
  const d = digitsOnly(v).slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0,2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8)}`;
  return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12)}`;
}
function formatCEP(v: string) {
  const d = digitsOnly(v).slice(0, 8);
  return d.length <= 5 ? d : `${d.slice(0,5)}-${d.slice(5)}`;
}
function formatPhone(v: string) {
  const d = digitsOnly(v).slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0,2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
  return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
}

// ── Animation — mesma config do Login ────────────────────────────────────

const SPRING: [number, number, number, number] = [0.34, 1.56, 0.64, 1];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.07, delayChildren: 0.1 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: SPRING } },
};

// ── Shared classes — mesmas do Login ─────────────────────────────────────

const inputClass =
  "h-12 rounded-xl border-white/[0.10] bg-white/[0.04] pl-11 text-white placeholder:text-slate-600 ring-1 ring-inset ring-white/[0.07] transition-all duration-200 focus-visible:border-teal-500/60 focus-visible:bg-white/[0.07] focus-visible:ring-teal-500/30 focus-visible:shadow-[0_0_0_3px_rgba(45,212,191,0.10)]";

const selTriggerClass =
  "h-12 rounded-xl border-white/[0.10] bg-white/[0.04] text-white ring-1 ring-inset ring-white/[0.07] transition-all duration-200 hover:bg-white/[0.07] focus:border-teal-500/60 focus:ring-teal-500/30 data-[placeholder]:text-slate-600";

// ── Helpers ────────────────────────────────────────────────────────────────

function Req() {
  return <span className="ml-0.5 text-teal-400" aria-label="obrigatório">*</span>;
}

function FieldError({ msg }: { msg?: string }) {
  if (!msg) return null;
  return (
    <motion.p
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.15 }}
      className="mt-1.5 flex items-center gap-1.5 text-xs text-red-400">
      <span className="inline-block h-1 w-1 shrink-0 rounded-full bg-red-400" />
      {msg}
    </motion.p>
  );
}

// Divisor de seção dentro do card — mesmo estilo do "Primeiro acesso" do Login
function SectionDivider({ label }: { label: string }) {
  return (
    <div className="relative">
      <div className="absolute inset-0 flex items-center">
        <div className="w-full border-t border-white/[0.08]" />
      </div>
      <div className="relative flex justify-start text-xs">
        <span className="rounded-md bg-[#0d1e30] pr-3 text-[11px] font-medium uppercase tracking-[0.1em] text-slate-600">
          {label}
        </span>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function InstitutionSignUp() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reducedMotion = usePrefersReducedMotion();

  const selectedPlanLabel = useMemo(() => {
    const p = searchParams.get("plano");
    return p && PLAN_LABELS[p] ? PLAN_LABELS[p] : null;
  }, [searchParams]);

  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Dados da instituição
  const [legalName, setLegalName] = useState("");
  const [tradeName, setTradeName] = useState("");
  const [taxId, setTaxId] = useState("");
  const [kind, setKind] = useState<InstitutionKind>("private_school");
  const [studentBand, setStudentBand] = useState("500-1500");

  // Endereço
  const [zipCode, setZipCode] = useState("");
  const [street, setStreet] = useState("");
  const [addressNumber, setAddressNumber] = useState("");
  const [complement] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [city, setCity] = useState("");
  const [stateUf, setStateUf] = useState("");

  // Responsável / conta
  const [contactName, setContactName] = useState("");
  const [contactRole, setContactRole] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showCPwd, setShowCPwd] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);

  // ── Validação ────────────────────────────────────────────────────────────
  const validateForm = (): boolean => {
    const next: Record<string, string> = {};
    if (!validators.name(legalName))
      next.legalName = "Informe a razão social (mín. 3 caracteres).";
    if (digitsOnly(taxId).length !== 14)
      next.taxId = "CNPJ deve ter exatamente 14 dígitos.";
    if (!validators.name(contactName))
      next.contactName = "Informe o nome completo (mín. 3 caracteres).";
    if (!validators.email(email))
      next.email = "E-mail inválido.";
    if (digitsOnly(phone).length < 10)
      next.phone = "Informe um telefone com DDD (mín. 10 dígitos).";
    if (!validators.password(password))
      next.password = "Senha deve ter pelo menos 8 caracteres.";
    if (!validators.passwordsMatch(password, confirmPassword))
      next.confirmPassword = "As senhas não coincidem.";
    if (!acceptPrivacy)
      next.acceptPrivacy = "Aceite a política de privacidade para continuar.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    setErrors({});

    const planInterest = searchParams.get("plano")?.trim() || "";

    // 1. Register institution (backend not yet live — will fail until Django endpoint is created)
    const instRes = await apiService.registerInstitution({
      legalName: legalName.trim(),
      tradeName: tradeName.trim(),
      taxId: digitsOnly(taxId),
      kind,
      zipCode: digitsOnly(zipCode) || undefined,
      street: street.trim() || undefined,
      addressNumber: addressNumber.trim() || undefined,
      complement: complement.trim() || undefined,
      neighborhood: neighborhood.trim() || undefined,
      city: city.trim(),
      state: stateUf.trim().toUpperCase().slice(0, 2),
      studentBand,
      contactName: contactName.trim(),
      contactRole: contactRole.trim(),
      email: email.trim(),
      phone: digitsOnly(phone),
      acceptPrivacy,
      interestPlan: planInterest || undefined,
    });

    if (!instRes.success) {
      setIsLoading(false);
      setErrors({ submit: instRes.error || "Não foi possível enviar o cadastro. Tente novamente." });
      return;
    }

    // 2. Create user account (endpoint exists in backend)
    const accRes = await apiService.signUp({
      name: contactName.trim(),
      email: email.trim(),
      password,
      confirmPassword,
      userType: "teacher",
      institutionCode: "",
    });

    setIsLoading(false);
    if (accRes.success) {
      setSuccess(true);
    } else {
      setErrors({ submit: accRes.error || "Cadastro enviado, mas houve um problema ao criar a conta. Entre em contato." });
    }
  };

  // ── Success ──────────────────────────────────────────────────────────────
  if (success) {
    return (
      <AuthPageChrome backLabel="Voltar ao início">
        <div className="flex min-h-[calc(100vh-57px)] items-center justify-center px-4 py-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.65, ease: SPRING }}
            className="w-full max-w-lg">
            <div className="relative overflow-hidden rounded-2xl border border-teal-500/25 bg-gradient-to-b from-[#0d1e30]/90 to-[#070d18]/95 p-10 text-center shadow-[0_32px_80px_-20px_rgba(0,0,0,0.85),0_0_0_1px_rgba(45,212,191,0.08),inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-2xl">
              <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px"
                style={{ background: "linear-gradient(90deg, transparent 0%, rgba(45,212,191,0.6) 40%, rgba(6,182,212,0.5) 60%, transparent 100%)" }} />
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl border border-teal-400/30 bg-teal-500/15">
                <CheckCircle2 className="h-8 w-8 text-teal-400" aria-hidden />
              </div>
              <h1 className="font-display text-2xl font-bold tracking-tight text-white">Cadastro enviado!</h1>
              <p className="mt-4 text-base leading-relaxed text-slate-400">
                Nossa equipe analisará os dados e entrará em contato no e-mail
                informado em até 2 dias úteis.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
                <Button variant="ghost"
                  className="h-11 rounded-xl border border-white/[0.12] bg-white/[0.04] text-slate-300 hover:bg-white/[0.09] hover:text-white"
                  onClick={() => navigate(ROUTES.HOME)}>
                  Voltar ao início
                </Button>
                <Button className="landing-cta-glow h-11 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 font-semibold hover:from-teal-400 hover:to-cyan-500"
                  onClick={() => navigate("/dashboard")}>
                  Ir para o painel <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </AuthPageChrome>
    );
  }

  // ── CNPJ state ────────────────────────────────────────────────────────────
  const cnpjDigits = digitsOnly(taxId);
  const cnpjComplete = cnpjDigits.length === 14;
  const cnpjPartial = cnpjDigits.length > 0 && !cnpjComplete;

  // ── Benefits — mesma estrutura do Login ──────────────────────────────────
  const benefits = [
    { icon: Brain,        title: "Perfis cognitivos sem caixa-preta",      text: "Questionário e comportamento geram um perfil auditável que alimenta ritmo e apresentação." },
    { icon: BarChart3,    title: "Painel de gestão em tempo real",          text: "Coordenadores acompanham progresso individual e por turma, com dados separados por papel." },
    { icon: Shield,       title: "LGPD na base",                            text: "Dados protegidos, transparência total e motivação desvinculada da avaliação formal." },
    { icon: GraduationCap, title: "Onboarding completo para professores",   text: "Suporte dedicado e documentação para toda a equipe pedagógica desde o primeiro dia." },
  ];

  return (
    <AuthPageChrome backLabel="Voltar ao início">
      {/* Mesmo glow do Login */}
      <div
        aria-hidden
        className="pointer-events-none fixed right-0 top-1/2 -translate-y-1/2 h-[600px] w-[520px] opacity-20"
        style={{
          background: "radial-gradient(ellipse at center, rgba(45,212,191,0.18) 0%, rgba(6,182,212,0.10) 35%, transparent 70%)",
          filter: "blur(40px)",
        }}
      />

      <div className="relative z-10 container mx-auto max-w-7xl px-4 pb-14 pt-8 sm:px-6 sm:pb-20 sm:pt-10">
        <div className="grid items-start gap-10 lg:grid-cols-[1fr_1.05fr] lg:gap-16 lg:items-start">

          {/* ── Coluna esquerda — mesmo padrão do Login ─────────────── */}
          <motion.div
            className="hidden min-w-0 lg:flex lg:flex-col lg:gap-6"
            variants={containerVariants}
            initial="hidden"
            animate="visible">

            {/* Headline + mascote lado a lado */}
            <motion.div variants={itemVariants} className="flex items-end gap-4">
              <div className="min-w-0 flex-1 space-y-4">
                <span className="badge-glow inline-flex items-center gap-1.5 rounded-full border border-teal-400/30 bg-teal-500/10 px-3 py-1 text-xs font-medium text-teal-200">
                  <Sparkles className="h-3.5 w-3.5" aria-hidden />
                  {selectedPlanLabel ? `Plano ${selectedPlanLabel}` : "Cadastro institucional"}
                </span>

                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-teal-400 to-cyan-500 shadow-lg shadow-teal-500/25">
                    <Brain className="h-5 w-5 text-white" aria-hidden />
                  </div>
                  <div>
                    <p className="font-display text-lg font-semibold tracking-tight text-white">NeuroMent</p>
                    <p className="text-sm text-teal-300/80">Personalização por perfil · gestão institucional</p>
                  </div>
                </div>

                <h1 className="font-display text-[2rem] font-bold leading-[1.12] tracking-tight text-white lg:text-[2.6rem]">
                  Gestão escolar
                  <span className="mt-1.5 block bg-gradient-to-r from-teal-300 via-cyan-200 to-sky-300 bg-clip-text text-transparent">
                    que se adapta
                  </span>
                </h1>
              </div>

              {/* Mascote — ao lado da headline */}
              <div className="relative shrink-0">
                <div
                  aria-hidden
                  className="pointer-events-none absolute -bottom-4 left-1/2 h-20 w-36 -translate-x-1/2"
                  style={{
                    background: "radial-gradient(ellipse, rgba(45,212,191,0.30) 0%, transparent 70%)",
                    filter: "blur(22px)",
                  }}
                />
                <motion.img
                  src={mascoteAnotando}
                  alt=""
                  aria-hidden
                  loading="lazy"
                  decoding="async"
                  fetchPriority="low"
                  className="relative h-52 w-auto select-none will-change-transform drop-shadow-[0_20px_40px_rgba(45,212,191,0.25)]"
                  animate={reducedMotion ? {} : { y: [0, -8, 0] }}
                  transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
                  draggable={false}
                />
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="flex items-center gap-3">
              <div className="h-px flex-1 bg-gradient-to-r from-teal-400/40 to-transparent" />
              <span className="text-xs text-slate-600">NeuroMent · Institucional</span>
            </motion.div>

            <motion.p variants={itemVariants} className="max-w-lg text-base leading-relaxed text-slate-400">
              Preencha o formulário e nossa equipe preparará uma demonstração
              personalizada. O CNPJ é obrigatório para verificar o vínculo institucional.
            </motion.p>

            <motion.ul variants={containerVariants} className="space-y-3">
              {benefits.map((item) => (
                <motion.li key={item.title} variants={itemVariants} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-teal-500/20 bg-teal-500/[0.08]">
                    <item.icon className="h-4 w-4 text-teal-400" aria-hidden />
                  </span>
                  <div className="min-w-0 pt-0.5">
                    <p className="text-sm font-medium text-slate-200">{item.title}</p>
                    <p className="mt-0.5 text-sm leading-snug text-slate-500">{item.text}</p>
                  </div>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>

          {/* ── Card do formulário — mesmo padrão do Login ──────────── */}
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, ease: SPRING, delay: 0.08 }}>

            <div className="relative overflow-hidden rounded-2xl border border-white/[0.09] bg-gradient-to-b from-[#0d1e30]/85 to-[#070d18]/90 p-7 shadow-[0_32px_80px_-20px_rgba(0,0,0,0.85),0_0_0_1px_rgba(45,212,191,0.06),inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-2xl sm:p-9">

              {/* Top edge highlight */}
              <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px"
                style={{ background: "linear-gradient(90deg, transparent 0%, rgba(45,212,191,0.55) 40%, rgba(6,182,212,0.45) 60%, transparent 100%)" }} />

              {/* Corner glow */}
              <div aria-hidden className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full opacity-20"
                style={{ background: "radial-gradient(circle, rgba(45,212,191,0.25) 0%, transparent 70%)" }} />

              <motion.div
                className="relative space-y-5"
                variants={containerVariants}
                initial="hidden"
                animate="visible">

                {/* Card header */}
                <motion.div variants={itemVariants} className="space-y-1">
                  <div className="mb-3 inline-flex items-center gap-1.5 rounded-md border border-white/[0.10] bg-white/[0.05] px-2.5 py-1 text-xs text-slate-400 lg:hidden">
                    <Brain className="h-3.5 w-3.5 text-teal-400" aria-hidden />
                    NeuroMent Institucional
                  </div>
                  <h2 className="font-display text-xl font-semibold tracking-tight text-white sm:text-2xl">
                    Cadastro de instituição
                  </h2>
                  <p className="text-sm leading-relaxed text-slate-500 sm:text-[0.9rem]">
                    Escola, colégio ou rede. Após envio, entramos em contato em até 2 dias úteis.
                  </p>
                </motion.div>

                <motion.form variants={itemVariants} onSubmit={handleSubmit} noValidate className="space-y-5">

                  {/* ── Dados da instituição ─────────────────────────── */}
                  <div className="space-y-4">
                    <SectionDivider label="Dados da instituição" />

                    {/* Razão social */}
                    <div className="space-y-2">
                      <Label htmlFor="legalName" className="text-sm font-medium text-slate-300">
                        Razão social ou nome oficial<Req />
                      </Label>
                      <div className="relative">
                        <Building2 className="pointer-events-none absolute left-3.5 top-1/2 z-10 h-[1.05rem] w-[1.05rem] -translate-y-1/2 text-slate-500" aria-hidden />
                        <Input id="legalName" value={legalName} onChange={(e) => setLegalName(e.target.value)}
                          className={inputClass} placeholder="Ex.: Associação Educacional XYZ" autoComplete="organization" />
                      </div>
                      <FieldError msg={errors.legalName} />
                    </div>

                    {/* Nome fantasia + CNPJ — grid */}
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="tradeName" className="text-sm font-medium text-slate-300">Nome fantasia</Label>
                        <Input id="tradeName" value={tradeName} onChange={(e) => setTradeName(e.target.value)}
                          className={`${inputClass} pl-3`} placeholder="Como a escola é conhecida" />
                      </div>

                      {/* CNPJ */}
                      <div className="space-y-2">
                        <Label htmlFor="taxId" className="flex items-center gap-1.5 text-sm font-medium text-slate-300">
                          <Hash className="h-3.5 w-3.5 text-slate-500" aria-hidden />
                          CNPJ<Req />
                          <AnimatePresence>
                            {cnpjComplete && (
                              <motion.span
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0, opacity: 0 }}
                                transition={{ type: "spring", stiffness: 400, damping: 18 }}>
                                <BadgeCheck className="h-4 w-4 text-emerald-400" aria-label="CNPJ válido" />
                              </motion.span>
                            )}
                          </AnimatePresence>
                        </Label>
                        <div className="relative">
                          <Hash className="pointer-events-none absolute left-3.5 top-1/2 z-10 h-[1.05rem] w-[1.05rem] -translate-y-1/2 text-slate-500" aria-hidden />
                          <Input id="taxId" value={taxId} onChange={(e) => setTaxId(formatCNPJ(e.target.value))}
                            className={`${inputClass} pr-10 ${errors.taxId ? "border-red-500/50 ring-red-500/20" : ""}`}
                            placeholder="XX.XXX.XXX/XXXX-XX" inputMode="numeric" maxLength={18} />
                          {cnpjPartial && (
                            <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-[10px] font-bold tabular-nums text-slate-600">
                              {cnpjDigits.length}/14
                            </span>
                          )}
                        </div>
                        <FieldError msg={errors.taxId} />
                      </div>
                    </div>

                    {/* Tipo + Porte */}
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-300">Tipo de instituição</Label>
                        <Select value={kind} onValueChange={(v) => setKind(v as InstitutionKind)}>
                          <SelectTrigger className={selTriggerClass}><SelectValue /></SelectTrigger>
                          <SelectContent className="rounded-xl border-white/[0.10] bg-[#0d1e30] text-white backdrop-blur-xl">
                            {KIND_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} className="focus:bg-teal-500/15 focus:text-white">{o.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-300">Número de alunos</Label>
                        <Select value={studentBand} onValueChange={setStudentBand}>
                          <SelectTrigger className={selTriggerClass}><SelectValue /></SelectTrigger>
                          <SelectContent className="rounded-xl border-white/[0.10] bg-[#0d1e30] text-white backdrop-blur-xl">
                            {STUDENT_BANDS.map((o) => <SelectItem key={o.value} value={o.value} className="focus:bg-teal-500/15 focus:text-white">{o.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* ── Endereço ─────────────────────────────────────── */}
                  <div className="space-y-4">
                    <SectionDivider label="Endereço" />

                    <div className="grid gap-4 sm:grid-cols-[1fr_80px]">
                      <div className="space-y-2">
                        <Label htmlFor="zipCode" className="text-sm font-medium text-slate-300">CEP</Label>
                        <div className="relative">
                          <MapPin className="pointer-events-none absolute left-3.5 top-1/2 z-10 h-[1.05rem] w-[1.05rem] -translate-y-1/2 text-slate-500" aria-hidden />
                          <Input id="zipCode" value={zipCode} onChange={(e) => setZipCode(formatCEP(e.target.value))}
                            className={inputClass} placeholder="00000-000" inputMode="numeric" maxLength={9} />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="stateUf" className="text-sm font-medium text-slate-300">UF</Label>
                        <Input id="stateUf" value={stateUf} onChange={(e) => setStateUf(e.target.value)}
                          maxLength={2} className={`${inputClass} pl-3 uppercase`} placeholder="SP" autoComplete="address-level1" />
                      </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-[1fr_100px]">
                      <div className="space-y-2">
                        <Label htmlFor="street" className="text-sm font-medium text-slate-300">Logradouro</Label>
                        <Input id="street" value={street} onChange={(e) => setStreet(e.target.value)}
                          className={`${inputClass} pl-3`} placeholder="Rua, Avenida, Alameda…" autoComplete="street-address" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="addressNumber" className="text-sm font-medium text-slate-300">Número</Label>
                        <Input id="addressNumber" value={addressNumber} onChange={(e) => setAddressNumber(e.target.value)}
                          className={`${inputClass} pl-3`} placeholder="1200" />
                      </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="neighborhood" className="text-sm font-medium text-slate-300">Bairro</Label>
                        <Input id="neighborhood" value={neighborhood} onChange={(e) => setNeighborhood(e.target.value)}
                          className={`${inputClass} pl-3`} autoComplete="address-level3" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="city" className="text-sm font-medium text-slate-300">Cidade</Label>
                        <Input id="city" value={city} onChange={(e) => setCity(e.target.value)}
                          className={`${inputClass} pl-3`} placeholder="São Paulo" autoComplete="address-level2" />
                      </div>
                    </div>
                  </div>

                  {/* ── Responsável ──────────────────────────────────── */}
                  <div className="space-y-4">
                    <SectionDivider label="Responsável" />

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="contactName" className="text-sm font-medium text-slate-300">Nome completo<Req /></Label>
                        <div className="relative">
                          <User className="pointer-events-none absolute left-3.5 top-1/2 z-10 h-[1.05rem] w-[1.05rem] -translate-y-1/2 text-slate-500" aria-hidden />
                          <Input id="contactName" value={contactName} onChange={(e) => setContactName(e.target.value)}
                            className={`${inputClass} ${errors.contactName ? "border-red-500/50 ring-red-500/20" : ""}`}
                            autoComplete="name" placeholder="Seu nome completo" />
                        </div>
                        <FieldError msg={errors.contactName} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contactRole" className="text-sm font-medium text-slate-300">Cargo / função</Label>
                        <Input id="contactRole" value={contactRole} onChange={(e) => setContactRole(e.target.value)}
                          className={`${inputClass} pl-3`} placeholder="Direção, Coordenação, TI"
                          autoComplete="organization-title" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-sm font-medium text-slate-300">E-mail institucional<Req /></Label>
                        <div className="relative">
                          <Mail className="pointer-events-none absolute left-3.5 top-1/2 z-10 h-[1.05rem] w-[1.05rem] -translate-y-1/2 text-slate-500" aria-hidden />
                          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                            className={`${inputClass} ${errors.email ? "border-red-500/50 ring-red-500/20" : ""}`}
                            autoComplete="email" placeholder="nome@escola.edu.br" />
                        </div>
                        <FieldError msg={errors.email} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-sm font-medium text-slate-300">Telefone / WhatsApp<Req /></Label>
                        <div className="relative">
                          <Phone className="pointer-events-none absolute left-3.5 top-1/2 z-10 h-[1.05rem] w-[1.05rem] -translate-y-1/2 text-slate-500" aria-hidden />
                          <Input id="phone" type="tel" value={phone} onChange={(e) => setPhone(formatPhone(e.target.value))}
                            className={`${inputClass} ${errors.phone ? "border-red-500/50 ring-red-500/20" : ""}`}
                            placeholder="(11) 99999-0000" autoComplete="tel" />
                        </div>
                        <FieldError msg={errors.phone} />
                      </div>
                    </div>
                  </div>

                  {/* ── Acesso (senha) ────────────────────────────────── */}
                  <div className="space-y-4">
                    <SectionDivider label="Acesso" />
                    <p className="text-xs text-slate-600">
                      Seu e-mail institucional será usado como login.
                    </p>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="password" className="text-sm font-medium text-slate-300">Senha<Req /></Label>
                        <div className="relative">
                          <Lock className="pointer-events-none absolute left-3.5 top-1/2 z-10 h-[1.05rem] w-[1.05rem] -translate-y-1/2 text-slate-500" aria-hidden />
                          <Input id="password" type={showPwd ? "text" : "password"} value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className={`${inputClass} pr-11 ${errors.password ? "border-red-500/50 ring-red-500/20" : ""}`}
                            placeholder="Mín. 8 caracteres" autoComplete="new-password" />
                          <button type="button" onClick={() => setShowPwd(!showPwd)}
                            className="absolute right-3.5 top-1/2 z-10 -translate-y-1/2 text-slate-500 transition-colors hover:text-slate-300 focus-visible:outline-none"
                            aria-label={showPwd ? "Ocultar senha" : "Mostrar senha"}>
                            {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        <FieldError msg={errors.password} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirmPassword" className="text-sm font-medium text-slate-300">Confirmar senha<Req /></Label>
                        <div className="relative">
                          <Lock className="pointer-events-none absolute left-3.5 top-1/2 z-10 h-[1.05rem] w-[1.05rem] -translate-y-1/2 text-slate-500" aria-hidden />
                          <Input id="confirmPassword" type={showCPwd ? "text" : "password"} value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className={`${inputClass} pr-11 ${errors.confirmPassword ? "border-red-500/50 ring-red-500/20" : ""}`}
                            placeholder="Repita a senha" autoComplete="new-password" />
                          <button type="button" onClick={() => setShowCPwd(!showCPwd)}
                            className="absolute right-3.5 top-1/2 z-10 -translate-y-1/2 text-slate-500 transition-colors hover:text-slate-300 focus-visible:outline-none"
                            aria-label={showCPwd ? "Ocultar confirmação" : "Mostrar confirmação"}>
                            {showCPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        <FieldError msg={errors.confirmPassword} />
                      </div>
                    </div>

                  </div>

                  {/* ── LGPD + CTA ───────────────────────────────────── */}
                  <div className="space-y-4">

                    {/* Privacy */}
                    <div className={`flex items-start gap-3 rounded-xl border p-3.5 transition-colors duration-200 ${
                      errors.acceptPrivacy ? "border-red-500/35 bg-red-500/[0.04]" : "border-white/[0.08] bg-white/[0.02]"
                    }`}>
                      <Checkbox id="privacy" checked={acceptPrivacy}
                        onCheckedChange={(c) => setAcceptPrivacy(c === true)}
                        className="mt-0.5 shrink-0 rounded-md border-white/25 bg-white/5 data-[state=checked]:border-teal-500 data-[state=checked]:bg-teal-600" />
                      <div className="min-w-0">
                        <label htmlFor="privacy" className="cursor-pointer text-sm font-normal leading-relaxed text-slate-400">
                          Declaro que tenho poderes para representar esta instituição e aceito o tratamento dos dados conforme a <span className="text-teal-400/80">política de privacidade</span> e a LGPD.<Req />
                        </label>
                        <FieldError msg={errors.acceptPrivacy} />
                      </div>
                    </div>

                    {/* Submit error */}
                    <AnimatePresence>
                      {errors.submit && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.97 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0 }}
                          className="rounded-xl border border-red-500/30 bg-red-500/[0.08] px-4 py-3 text-sm text-red-300">
                          {errors.submit}
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* CTA — mesmo estilo do Login */}
                    <Button
                      type="submit"
                      size="lg"
                      className="landing-cta-glow h-12 w-full rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 text-sm font-semibold shadow-xl shadow-teal-950/50 hover:from-teal-400 hover:to-cyan-500 sm:h-14 sm:text-base"
                      disabled={isLoading}>
                      {isLoading ? (
                        <><Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden />Enviando cadastro…</>
                      ) : (
                        "Cadastrar Instituição"
                      )}
                    </Button>
                  </div>

                </motion.form>

                {/* Divider + links — mesmo padrão do Login */}
                <motion.div variants={itemVariants} className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/[0.08]" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="rounded-md bg-[#0d1e30] px-3 py-0.5 text-slate-600 ring-1 ring-inset ring-white/[0.06]">
                      Já tem conta?
                    </span>
                  </div>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Button
                    type="button"
                    onClick={() => navigate("/login")}
                    variant="ghost"
                    size="lg"
                    className="h-12 w-full rounded-xl border border-white/[0.12] bg-white/[0.04] text-sm font-medium text-slate-300 hover:bg-white/[0.08] hover:text-white sm:h-14">
                    Entrar na conta
                  </Button>
                </motion.div>

                <motion.p variants={itemVariants}
                  className="text-center text-[11px] leading-relaxed text-slate-600 sm:text-xs">
                  Cadastro de estudante?{" "}
                  <Link to="/signup" className="text-teal-500/70 transition-colors hover:text-teal-400">
                    Criar conta piloto
                  </Link>
                  {" · "}
                  <Link to={ROUTES.HOME} className="text-teal-500/70 transition-colors hover:text-teal-400">
                    Voltar ao início
                  </Link>
                </motion.p>

              </motion.div>
            </div>
          </motion.div>

        </div>
      </div>
    </AuthPageChrome>
  );
}
